import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import LetsTalkCta from '../LetsTalkCta'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react.js'
import 'swiper/swiper.scss'
import {
  TOOL_CATEGORIES,
  TOOL_CATEGORY_ORDER,
  TOOLS_SORTED,
} from '../../constants/toolsCatalog'
import arrowLeftBlue from '../../assets/images/arrow-left-blue.svg'
import arrowRightBlue from '../../assets/images/arrow-right-blue.svg'

const HomeTwo = ({ lang }) => {
  const isEn = lang === 'en'
  const [activeFilter, setActiveFilter] = useState('all')
  const swiperRef = useRef(null)

  const toolCategories = useMemo(
    () => TOOL_CATEGORY_ORDER.map(key => TOOL_CATEGORIES[key]),
    []
  )

  const filteredTechnologies = useMemo(() => {
    if (activeFilter === 'all') return TOOLS_SORTED
    return TOOLS_SORTED.filter(t => t.category === activeFilter)
  }, [activeFilter])

  const setSwiper = useCallback(swiper => {
    swiperRef.current = swiper
    window.requestAnimationFrame(() => {
      swiper.update()
    })
  }, [])

  useEffect(() => {
    const s = swiperRef.current
    if (!s) return
    const id = window.requestAnimationFrame(() => {
      s.slideTo(0, 0)
      s.update()
    })
    return () => window.cancelAnimationFrame(id)
  }, [activeFilter])

  const goPrev = useCallback(() => {
    const s = swiperRef.current
    if (s) {
      s.slidePrev()
    }
  }, [])

  const goNext = useCallback(() => {
    const s = swiperRef.current
    if (s) {
      s.slideNext()
    }
  }, [])

  const prevLabel = isEn ? 'Previous tools' : 'Ferramentas anteriores'
  const nextLabel = isEn ? 'Next tools' : 'Ferramentas seguintes'

  return (
    <section id="home-two" className="bg-white home-two-tools">
      <div className="content container ">
        <div className="home-two-tools__header">
          <div className="home-two-tools__heading-col">
            <h2 className="home-two-tools__title">
              <span className="home-two-tools__title-accent">
                {isEn ? 'Our Tools' : 'Ferramentas'}
              </span>
              <span className="home-two-tools__title-rest">
                {isEn ? ' of choice.' : ' de eleição.'}
              </span>
            </h2>
          </div>
          <div className="home-two-tools__intro">
            <p className="home-two-tools__lede">
              {isEn
                ? 'We approach our challenges with an agile mindset and practices, and we are always open to reconsider our options. That said, there are a few tools that we typically choose first when it comes to our development environment.'
                : 'Abordamos os nossos desafios com uma mentalidade e práticas ágeis, e estamos sempre abertos a reavaliar as nossas opções. No entanto, existem algumas ferramentas que são as nossas primeiras escolhas quando se trata do nosso ambiente de desenvolvimento.'}
            </p>
            <LetsTalkCta
              isEn={isEn}
              hex="#322EFF"
              tone="accent"
              paintDrip
              to={isEn ? '/en/toolbox/' : '/ferramentas/'}
              label={isEn ? 'Our Toolbox' : 'As nossas ferramentas'}
              className="home-two-tools__toolbox-cta"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 home-two-tools__filters-wrap">
            <div
              className="filters filters-technologies"
              role="group"
              aria-label={
                isEn ? 'Tool categories' : 'Categorias de ferramentas'
              }
            >
              <button
                type="button"
                className={`filter tech-filter home-two-tools__tab${
                  activeFilter === 'all' ? ' active' : ''
                }`}
                aria-pressed={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
              >
                {isEn ? 'All Tools' : 'Tudo'}
              </button>
              {toolCategories.map(techCat => (
                <button
                  type="button"
                  key={techCat.value}
                  className={`filter tech-filter home-two-tools__tab notranslate${
                    activeFilter === techCat.value ? ' active' : ''
                  }`}
                  aria-pressed={activeFilter === techCat.value}
                  onClick={() => setActiveFilter(techCat.value)}
                  translate="no"
                >
                  {techCat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="col-12 swiper-technologies">
            <div className="home-two-tools__carousel">
              <button
                type="button"
                className="home-two-tools__carousel-arrow home-two-tools__carousel-arrow--prev"
                aria-label={prevLabel}
                onClick={goPrev}
              >
                <img
                  src={arrowLeftBlue}
                  alt=""
                  width={22}
                  height={22}
                  decoding="async"
                  aria-hidden="true"
                  className="home-two-tools__carousel-arrow-img"
                />
              </button>
              <div className="home-two-tools__carousel-viewport">
                <Swiper
                  onSwiper={setSwiper}
                  spaceBetween={32}
                  slidesPerView="auto"
                  observer
                  observeParents
                  resizeObserver
                  watchOverflow
                  className="swiper-technologies-slider"
                >
                  {filteredTechnologies.map(technology => (
                    <SwiperSlide
                      key={technology.title}
                      className="tech-slide"
                      data-tech-category={technology.category}
                    >
                      <div className="technology-item">
                        <img
                          src={technology.image}
                          alt={technology.title}
                          loading="lazy"
                          width={88}
                          height={88}
                          decoding="async"
                          draggable={false}
                        />
                        <span className="technology-item__label">
                          {technology.title}
                        </span>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <button
                type="button"
                className="home-two-tools__carousel-arrow home-two-tools__carousel-arrow--next"
                aria-label={nextLabel}
                onClick={goNext}
              >
                <img
                  src={arrowRightBlue}
                  alt=""
                  width={22}
                  height={22}
                  decoding="async"
                  aria-hidden="true"
                  className="home-two-tools__carousel-arrow-img"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HomeTwo
